import { withDefault } from "collection-utils";
import { BaseFieldMaterialRenderer } from "./base-field-material-renderer";
import type { Value, PotentialValue } from "common/components/types";
import { asString, loadedDefinedMap, asNumber } from "common/components/component-helpers";
import { parseNumber } from "common/Support";
import { getLocalizedString, formatLocalizedString } from "common/components/localization";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { convertInForNumberField } from "generator/components/number-field";
import { isPrimitiveValue } from "common/components/computation-types";
import { definedMap } from "@glideapps/ts-necessities";

export const NumberFieldMaterialRenderer: GlideFunctionalRenderer = p => {
    const convertIn = (input: Value): string => {
        if (!isPrimitiveValue(input)) return "";
        return definedMap(convertInForNumberField(input), n => n.toString()) ?? "";
    };

    const convertOut = (output: string): Value => {
        return withDefault<Value>(parseNumber(output), output);
    };

    const getErrorMessage = (
        input: string,
        isRequired: boolean,
        args: readonly PotentialValue[]
    ): string | undefined => {
        const num = parseNumber(input);

        if (num === undefined) {
            if (isRequired) {
                return getLocalizedString("required", p.env.appEnvironment.appKind);
            }
            return undefined;
        }

        const [, , , , , , , , , minValue, maxValue] = args;
        const min = loadedDefinedMap(minValue, asNumber);
        const max = loadedDefinedMap(maxValue, asNumber);

        if (min !== undefined && num < min) {
            return formatLocalizedString("mustBeGreaterThan", [min.toString()]);
        } else if (max !== undefined && num > max) {
            return formatLocalizedString("mustBeLessThan", [max.toString()]);
        }

        return undefined;
    };

    const getPrefixSuffix = (args: readonly PotentialValue[]): [string | undefined, string | undefined] => {
        const [, , , , , , , , , , , prefixValue, suffixValue] = args;
        const prefix = loadedDefinedMap(prefixValue, asString);
        const suffix = loadedDefinedMap(suffixValue, asString);
        return [prefix, suffix];
    };

    return (
        <BaseFieldMaterialRenderer
            {...p}
            dataType="number"
            convertIn={convertIn}
            convertOut={convertOut}
            getErrorMessage={getErrorMessage}
            getPrefixSuffix={getPrefixSuffix}
        />
    );
};
